import React, { useState } from 'react';
import { signIn, getCurrentUser, resendSignUpCode } from '@aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  background: url('/images/hero-2.jpeg') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    padding: 0 var(--spacing-md);
    min-height: 100vh;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  color: white;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-xl);
  }
`;

const SignInCard = styled.div`
  position: relative;
  z-index: 2;
  width: 400px;
  background: rgba(255, 255, 255, 0.95);
  padding: var(--spacing-xl);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: var(--spacing-lg);
  }
  
  @media (max-width: 480px) {
    padding: var(--spacing-md);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 50px;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: var(--spacing-sm);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1.2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--primary-dark);
  }
  
  &:disabled {
    background: var(--text-secondary);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const LinkText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignIn = ({ onSignIn, onNewPasswordRequired }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  function getCognitoErrorCode(error) {
    // Prefer error.code, then error.name, then custom properties, then fallback to string matching
    if (error?.code) return error.code;
    if (error?.name) return error.name;
    if (error?.['x-amzn-errortype']) return error['x-amzn-errortype'];
    return '';
  }

  function getCognitoErrorMessage(error) {
    if (error?.message) return error.message;
    if (error?.['x-amzn-errormessage']) return error['x-amzn-errormessage'];
    return '';
  }

  function isUnconfirmedError(error) {
    const code = getCognitoErrorCode(error).toLowerCase();
    const msg = getCognitoErrorMessage(error).toLowerCase();
    if (code === 'usernotconfirmedexception') return true;
    if (msg === 'user is not confirmed.') return true;
    // Fallback to string matching for legacy/unknown errors
    const errorString = msg + ' ' + code + ' ' + JSON.stringify(error || '');
    const lower = errorString.toLowerCase();
    return (
      lower.includes('not confirmed') ||
      lower.includes('unconfirmed') ||
      lower.includes('not verified') ||
      lower.includes('user is not confirmed') ||
      lower.includes('email not verified')
    );
  }

  function isInvalidCredentialsError(error) {
    const code = getCognitoErrorCode(error).toLowerCase();
    const msg = getCognitoErrorMessage(error).toLowerCase();
    if (code === 'usernotfoundexception' || code === 'notauthorizedexception') return true;
    if (msg === 'incorrect username or password.') return true;
    // Fallback to string matching for legacy/unknown errors
    const errorString = msg + ' ' + code + ' ' + JSON.stringify(error || '');
    const lower = errorString.toLowerCase();
    return (
      lower.includes('incorrect username or password') ||
      lower.includes('username/client id combination not found') ||
      (lower.includes('username') && lower.includes('not found')) ||
      (lower.includes('username') && lower.includes('combination not found'))
    );
  }

  function isAttemptLimitError(error) {
    const code = getCognitoErrorCode(error).toLowerCase();
    const msg = getCognitoErrorMessage(error).toLowerCase();
    if (code === 'toomanyfailedattemptsexception') return true;
    if (msg.includes('attempt limit exceeded')) return true;
    const errorString = msg + ' ' + code + ' ' + JSON.stringify(error || '');
    return errorString.toLowerCase().includes('attempt limit exceeded');
  }

  function mapErrorMessage(error) {
    if (isInvalidCredentialsError(error)) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (isAttemptLimitError(error)) {
      return 'Too many failed attempts. Please wait 1 minute before trying again.';
    }
    return 'Sign in failed: ' + (getCognitoErrorMessage(error) || 'Unknown error');
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    try {
      const currentUser = await getCurrentUser().catch(() => null);
      if (!currentUser) {
        let isSignedIn = false;
        let nextStep = null;
        let signInError = null;
        try {
          const result = await signIn({ username, password });
          isSignedIn = result.isSignedIn;
          nextStep = result.nextStep;
        } catch (error) {
          console.error('Sign in error:', error.message);
          isSignedIn = false;
          nextStep = null;
          signInError = error;
        }
        if (isSignedIn) {
          onSignIn();
        } else if (nextStep && nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          console.log('New password required, redirecting to new-password');
          onNewPasswordRequired(nextStep);
          navigate('/new-password');
        } else if (nextStep && nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          console.log('Account needs confirmation, attempting to resend verification code');
          try {
            await resendSignUpCode({ username });
            console.log('Successfully resent verification code, redirecting to confirm-signup');
            navigate(`/confirm-signup/${encodeURIComponent(username)}`, {
              state: {
                message: 'We detected your account needs verification. A new verification code has been sent to your email! Please use your original password when signing in after verification.'
              }
            });
            setIsProcessing(false);
            return;
          } catch (resendError) {
            console.error('Failed to resend verification code:', resendError.message);
            setError('Failed to resend verification code. Please try again or contact support.');
            setIsProcessing(false);
            return;
          }
        } else if (signInError) {
          if (isInvalidCredentialsError(signInError)) {
            setError(mapErrorMessage(signInError));
            setIsProcessing(false);
            return;
          } else if (isUnconfirmedError(signInError)) {
            console.log('Detected unconfirmed account, attempting to resend verification code');
            try {
              await resendSignUpCode({ username });
              console.log('Successfully resent verification code, redirecting to confirm-signup');
              navigate(`/confirm-signup/${encodeURIComponent(username)}`, {
                state: {
                  message: 'We detected your account needs verification. A new verification code has been sent to your email! Please use your original password when signing in after verification.'
                }
              });
              setIsProcessing(false);
              return;
            } catch (resendError) {
              console.error('Failed to resend verification code:', resendError.message);
              setError(mapErrorMessage(resendError));
              setIsProcessing(false);
              return;
            }
          } else {
            setError(mapErrorMessage(signInError));
            setIsProcessing(false);
            return;
          }
        } else {
          console.error('No sign in error but also no success - this should not happen');
          setError('Sign in failed: Unknown error');
          setIsProcessing(false);
          return;
        }
      } else {
        onSignIn();
      }
    } catch (error) {
      console.error('Outer catch error:', error.message);
      setError(mapErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <h1>Empowering Young Minds Through Continuous Learning</h1>
          <p>Join thousands of students achieving their academic goals with BrainyHoot's 360° learning experience.</p>
        </HeroContent>
        
        <SignInCard>
          <h2 style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>Sign In</h2>
          <Form onSubmit={handleSignIn}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Input
              type="text"
              placeholder="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            
            <PasswordInputContainer>
              <PasswordInput
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  // Open Eye SVG
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" />
                    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth={2} />
                  </svg>
                ) : (
                  // Closed Eye SVG
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M1.5 12s3.5-7 10.5-7c2.5 0 4.5.5 6.5 1.5M22.5 12s-3.5 7-10.5 7c-2.5 0-4.5-.5-6.5-1.5M9.5 9.5a3.5 3.5 0 004.95 4.95" />
                  </svg>
                )}
              </PasswordToggle>
            </PasswordInputContainer>
            
            <Button type="submit" disabled={isProcessing}>
              {isProcessing ? 'Signing In...' : 'Sign In'}
            </Button>
            
            <LinkText>
              <Link to="/reset-password">Forgot your password?</Link>
            </LinkText>
            
            <LinkText>
              Don't have an account? <Link to="/signup">Sign up here</Link>
            </LinkText>
          </Form>
        </SignInCard>
      </HeroSection>

      {/* Features Section */}
      <div className="container">
        <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>Why Choose BrainyHoot?</h2>
        <div className="grid" style={{ gap: 'var(--spacing-xl)' }}>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Comprehensive Assessments
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Access a wide range of carefully curated questions designed to enhance your real world problem-solving skills.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Continuous Growth
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Gain insights from every assessment to drive continuous learning and long-term growth.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Expert Support
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Our team of experienced educators is here to guide you through your learning journey.
            </p>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div style={{ 
        backgroundColor: 'white',
        padding: 'var(--spacing-2xl) 0',
        marginTop: 'var(--spacing-2xl)'
      }}>
        <div className="container">
          <div style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: 'var(--spacing-xl)' }}>About BrainyHoot</h2>
            <p style={{ 
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              fontSize: 'var(--font-size-lg)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              At BrainyHoot, we're transforming the way students learn—combining technology, innovation, and expert guidance to support continuous, meaningful education.
              Our mission goes beyond exam preparation. We aim to nurture a deep understanding of concepts and their real-world applications, empowering students to learn for life, not just for exams.
              <br /><br />
              Quality learning experiences, accessible to all—that's our promise.
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--spacing-xl)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🎯
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Personalized Learning
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Adaptive tests tailored to your learning pace
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  💡
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Continuous Growth
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Regular updates with new content and features
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🤝
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Expert Support
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Dedicated team to assist your learning journey
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SignIn;